<template>
  <span>
    <div v-if="error" class="text-center my-5">
      <p class="text-secondary">{{$t('common.error_fetch_data')}}</p>
      <p class="text-primary link" href="#" @click.prevent="fetchData">{{$t('common.rfrsh_page')}} <i class="icon fa fa-refresh"></i></p>
    </div>
    <div v-else>
      <StatCards :cards="cards" />
      <b-container fluid class="my-5">
        <HorizontalBarChart :chartData="chartData" :options="options" v-if="chartData" />
      </b-container>
      <b-table striped hover bordered :items="items" :fields="fields" sort-by="vendeur" :busy="!items">
        <template #cell(repartition)="data">
          <RepartitionBar
            :total="data.value.total"
            :facture="data.value.facture"
            :paye="data.value.paye"
            :reverse="data.value.reverse"
            :non_facture="data.value.non_facture"
          />
        </template>
        <template #table-busy>
          <div class="text-center greenFont my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>{{$t('common.chrg_data')}}</strong>
          </div>
        </template>
      </b-table>
    </div>
  </span>
</template>

<script>
import HorizontalBarChart from '@/pages/statistics/components/graphiques/HorizontalBarChart.vue'
import StatCards from '@/pages/statistics/components/MarketPlace/StatCards.vue'
import RepartitionBar from '@/pages/statistics/components/MarketPlace/RepartitionBar.vue'

export default {
  name: 'MarketPlaceStats',
  data () {
    return {
      rawData: undefined,
      error: false,
      options: {
        indexAxis: 'x',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            stacked: true
          }],
          yAxes: [{
            stacked: true
          }]
        },
        tooltips: {
          filter: (tooltipItem, data) => tooltipItem.value !== '0'
        }
      },
      fields: [
        {
          key: 'vendeur',
          label: this.$t('monitoring.mp.table.vendeur'),
          sortable: true
        },
        {
          key: 'projet',
          label: this.$t('monitoring.mp.table.projet'),
          sortable: true
        },
        {
          key: 'nombre_de_factures',
          label: this.$t('monitoring.mp.table.nombre_de_factures'),
          sortable: true
        },
        {
          key: 'nombre_de_transactions',
          label: this.$t('monitoring.mp.table.nombre_de_transactions'),
          sortable: true
        },
        {
          key: 'montant_du_projet',
          label: this.$t('monitoring.mp.table.montant_du_projet'),
          sortable: true
        },
        {
          key: 'repartition',
          label: this.$t('monitoring.mp.table.repartition')
        }
      ]
    }
  },
  components: {
    HorizontalBarChart,
    StatCards,
    RepartitionBar
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.error = false
      this.$engine.monitoring.mp.getStats()
      .then(response => {
        if (response.error) {
          this.error = true
        } else {
          this.rawData = response
        }
      })
    }
  },
  computed: {
    chartData () {
      if (this.rawData) {
        return {
          labels: [this.$t('monitoring.mp.chart.total'), this.$t('monitoring.mp.chart.repartition')],
          datasets: [{
            axis: 'y',
            label: 'Montant total des projets',
            data: [this.rawData.Total.ProjectAmount, 0],
            backgroundColor: ['rgba(108, 117, 125, 0.8)', 'rgba(108, 117, 125, 0.8)'],
            borderWidth: 1,
            maxBarThickness: 50
          },
          {
            axis: 'y',
            label: 'Facturé',
            data: [0, this.rawData.Total.AmountFacture],
            backgroundColor: ['rgba(255, 193, 7, 0.8)', 'rgba(255, 193, 7, 0.8)'],
            borderWidth: 1,
            maxBarThickness: 50
          },
          {
            axis: 'y',
            label: 'Payé',
            data: [0, this.rawData.Total.AmountCashIn],
            backgroundColor: ['rgba(0, 123, 255, 0.8)', 'rgba(0, 123, 255, 0.8)'],
            borderWidth: 1,
            maxBarThickness: 50
          },
          {
            axis: 'y',
            label: 'Reversé',
            data: [0, this.rawData.Total.AmountCashOut],
            backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(40, 167, 69, 0.8)'],
            borderWidth: 1,
            maxBarThickness: 50
          },
          {
            axis: 'y',
            label: 'Non Facturé',
            data: [0, this.rawData.Total.AmountNonFacture],
            backgroundColor: ['#d4d4d4', '#d4d4d4'],
            borderWidth: 1,
            maxBarThickness: 50
          }]
        }
      }
    },
    items () {
      if (this.rawData) {
        return this.rawData.Stats.map(entry => {
          return {
            vendeur: entry.SellerName,
            projet: entry.ProjectName,
            nombre_de_factures: entry.CountInvoice,
            nombre_de_transactions: entry.CountTransaction,
            montant_du_projet: `${entry.ProjectAmount}€`,
            repartition: {
              total: entry.ProjectAmount,
              reverse: entry.AmountCashOut,
              paye: entry.AmountCashIn,
              facture: entry.AmountFacture,
              non_facture: entry.AmountNonFacture
            }
          }
        })
      }
    },
    cards () {
      if (this.rawData) {
        return [
          [
            {label: this.$t('monitoring.mp.cards.count_project'), value: this.rawData.Total.CountProject + ''},
            {label: this.$t('monitoring.mp.cards.count_invoice'), value: this.rawData.Total.CountInvoice + ''},
            {label: this.$t('monitoring.mp.cards.count_transaction'), value: this.rawData.Total.CountTransaction + ''},
            {label: this.$t('monitoring.mp.cards.amount_project'), value: this.rawData.Total.ProjectAmount + '€', variant: 'muted'}
          ],
          [
            {label: this.$t('monitoring.mp.cards.amount_facture'), value: this.rawData.Total.AmountFacture + '€', variant: 'warning'},
            {label: this.$t('monitoring.mp.cards.amount_paye'), value: this.rawData.Total.AmountCashIn + '€', variant: 'primary'},
            {label: this.$t('monitoring.mp.cards.amount_reverse'), value: this.rawData.Total.AmountCashOut + '€', variant: 'success'},
            {label: this.$t('monitoring.mp.cards.amount_non_facture'), value: this.rawData.Total.AmountNonFacture + '€', color: '#c4c4c4'}
          ]
        ]
      }
    }
  }
}
</script>

<style scoped>
.link:hover {
  text-decoration: underline;
  cursor:pointer;
}
</style>